// Label formats, subject types, and QR sizing.
//
// Session 75, built to docs/gates/Stede_LabelPrinting_Gate_Phase4.html.
//
// This module exists because of the gate's change 3: size profile and template
// are TWO AXES, not one. Before this, "which size" and "what goes on it" were
// the same decision, expressed as a boolean (isToolMode) plus a hardcoded pair
// of millimetre numbers at each call site. Seven sizes x one template worked as
// a boolean. Seven sizes x four templates does not.
//
// The two axes:
//   LABEL_FORMATS - a physical rectangle. Knows its size, what can print it,
//                   and how far it scans. Knows nothing about what it carries.
//   SUBJECT_TYPES - what is being labelled. Knows its route, its fields and
//                   which formats make sense for it. Knows nothing about mm.
//
// Adding Safety or Work Orders later is one SUBJECT_TYPES entry and a template,
// not a sweep of booleans that each fail silently. See the gate's section 6.

// ---------------------------------------------------------------------------
// Axis 1 - the format ladder
// ---------------------------------------------------------------------------

// Destination ids. `zebraFuture` on 4x6 is deliberate: the gate lists it as a
// future Zebra size, and recording it as a flag rather than omitting it is what
// stops a later session re-deriving whether 4x6 was considered.
export const DESTINATIONS = {
  // Session 76. Was `brother: { label: 'Brother PT-E560BT', verb: 'Prints now' }`
  // and both halves were claims the app cannot keep.
  //
  // The label named a specific machine that Stede has no way to identify. This
  // path is window.open() plus the browser's own print dialog, so the job goes
  // to whichever printer the OS is pointed at - Stede never learns which. The
  // verb then promised paper. Colby found it from his house, thirty miles from
  // the printer, being told it would print now.
  //
  // Availability came from `branches.has_brother_printer`, a hand-flipped
  // boolean meaning "this branch owns a Brother". That is a fact about an
  // equipment list, not about whether anything is plugged into this laptop, and
  // it gated the wrong way in both directions: unreachable at a branch whose
  // flag was never set even with the printer on USB, and offered at a branch
  // whose flag was set from anywhere on earth.
  //
  // No shipping browser API can do better. `navigator.printing` is a WICG
  // proposal and `chrome.printing.getPrinters()` needs a Chrome extension;
  // enumerating printers from a page is a fingerprinting vector browsers
  // deliberately close. Real presence needs a per-machine agent - Zebra's
  // Browser Print serves exactly this at localhost:9101, Brother's equivalent
  // is the b-PAC client plus an extension. That decision is logged, not taken.
  //
  // So the destination stops naming a printer and names the mechanism. The OS
  // print dialog IS the list of printers you currently have access to, and it
  // is the same list that let Colby print an email onto label tape.
  local:   { id: 'local',   label: "This computer's printer", verb: 'Opens your print dialog' },
  zebra:   { id: 'zebra',   label: 'Zebra ZD421',       verb: 'Sends to printer' },
  // D7: Stede downloads a file and delivers nothing. The verb stays honest
  // until an actual delivery path ships. Do not upgrade this to "Sent to
  // production" - that was in the gate's first draft and was a claim the app
  // cannot keep.
  canon:   { id: 'canon',   label: 'Canon Colorado M5', verb: 'PDF download' },
};

// The Brother tape constraint, in one place. 24mm tape, confirmed by Colby at
// 74z7 (#322). Length along the tape is free; width is not.
//
// Session 76: this no longer drives any disabled state. The `local` destination
// is whatever printer the OS is pointed at, which may not be a Brother at all,
// so blocking on tape width would be guessing about hardware Stede cannot see.
// Kept because it is a true fact about the machine at Dallas and the physical
// measurement owed against #322 is still owed.
export const BROTHER_TAPE_WIDTH_MM = 24;
export const BROTHER_TAPE_WIDTH_IN = BROTHER_TAPE_WIDTH_MM / 25.4; // ~0.945"

// Every rectangle Stede can print.
//
// widthIn is ALWAYS the first number in the label ("3 x 5" is 3 wide, 5 tall)
// and it is the dimension that tiles across the Canon roll. That orientation is
// not cosmetic - it is what makes the gate's own tiling figures come out: 16
// across for 3x5, 6 for 8.5x11, 2 at 24 inches wide, against 52.5in of usable
// roll. Swapping any pair silently changes how much vinyl a batch consumes.
//
// The small formats are landscape (2x1) and the large ones portrait (3x5), so
// the SHORT edge is heightIn for some and widthIn for others. Use shortEdgeIn()
// rather than assuming either.
//
// scanText/readText come from the gate's section 3 and are shown ON the size
// chips, not just in the document - callout A. The number is the warning.
export const LABEL_FORMATS = {
  '2x1': {
    id: '2x1', label: '2 × 1', widthIn: 2, heightIn: 1,
    scanText: 'scans 8 in', readText: 'reads close',
    blurb: 'Bin and shelf label. Scans from about 8 inches — hold the phone close.',
    destinations: ['local', 'zebra', 'canon'],
    onMobile: true,
  },
  '3x1': {
    id: '3x1', label: '3 × 1', widthIn: 3, heightIn: 1,
    scanText: 'scans 8 in', readText: 'reads close',
    blurb: 'Shelf edge, for longer names. Same scan range as 2 × 1 — the extra inch buys text room, not distance.',
    // Session 76: gains `local`. It was excluded from the old `brother` list
    // because no PT-E560BT profile declared a 3-inch length, not because the
    // machine cannot print one - length along 24mm tape is free, and the page
    // size now comes from this table rather than from LABEL_PROFILES.
    destinations: ['local', 'zebra', 'canon'],
    onMobile: true,
  },
  // The tool label. The gate's ladder has no 1x1 rung - the three small formats
  // are 2x1, 3x1 and this. It replaces the old pt_e560bt_1x1 compact profile,
  // which was too cramped to carry model, serial and station and fit only a QR
  // with a truncated serial under it.
  'tool_1_5x1': {
    id: 'tool_1_5x1', label: '1.5 × 1', widthIn: 1.5, heightIn: 1,
    scanText: 'scans 8 in', readText: 'reads in hand',
    blurb: 'Drill bodies and tool cases. TZe extra-strength adhesive, 24mm tape.',
    destinations: ['local'],
    onMobile: true,
  },
  '3x5': {
    id: '3x5', label: '3 × 5', widthIn: 3, heightIn: 5,
    scanText: 'scans 2 ft', readText: 'reads 10 ft',
    blurb: 'Carton and bin label. Scans from about 2 ft, text reads at 10 ft.',
    destinations: ['canon'],
    onMobile: true,
  },
  '4x6': {
    id: '4x6', label: '4 × 6', widthIn: 4, heightIn: 6,
    scanText: 'scans 3 ft', readText: 'reads 12 ft',
    blurb: 'Racks. The dominant warehouse thermal size.',
    destinations: ['canon'],
    zebraFuture: true,
    onMobile: true,
  },
  '8.5x11': {
    id: '8.5x11', label: '8.5 × 11', widthIn: 8.5, heightIn: 11,
    scanText: 'scans 6 ft', readText: 'reads 20 ft',
    blurb: 'Wall — find the right stack from across the floor. Reads at 20 ft but only scans at 6.',
    destinations: ['canon'],
    onMobile: true,
  },
  '24x30': {
    id: '24x30', label: '24 × 30', widthIn: 24, heightIn: 30,
    scanText: 'scans 15 ft', readText: 'reads 40 ft',
    blurb: 'The wall label. Scan over stacked stock without walking up to it.',
    destinations: ['canon'],
    // Callout K: deliberately desktop-only. Nobody specifies a two-and-a-half
    // foot wall poster from a phone while standing at a shelf. This is a scope
    // decision, recorded so a later session does not "fix" it.
    onMobile: false,
  },
};

// Ladder order for the picker. Object key order is not a contract; this is.
export const FORMAT_ORDER = ['2x1', '3x1', 'tool_1_5x1', '3x5', '4x6', '8.5x11', '24x30'];

// ---------------------------------------------------------------------------
// Axis 2 - the subject registry (gate change 2)
// ---------------------------------------------------------------------------

// Replaces `isToolMode`. Two entries today; Safety and Work Orders are drawn in
// the gate and are one entry each when their modules arrive.
//
// `route` is a function of the record id and is the single place a label's
// scan destination is decided. It is passed to labelTargetUrl() by the caller -
// this module deliberately does not import appUrl, so it stays testable and has
// no opinion about which host a label points at.
export const SUBJECT_TYPES = {
  inventory: {
    id: 'inventory',
    label: 'Inventory',
    domain: 'inventory',
    accent: '#1D9E75',
    route: (id) => `/inventory/scan/${id}`,
    formats: ['2x1', '3x1', '3x5', '4x6', '8.5x11', '24x30'],
    defaultFormat: '2x1',
    fields: ['name', 'sku', 'uom', 'branch'],
  },
  tool: {
    id: 'tool',
    label: 'Tools',
    domain: 'tools',
    accent: '#378ADD',
    route: (id) => `/tool/${id}`,
    formats: ['tool_1_5x1', '2x1'],
    defaultFormat: 'tool_1_5x1',
    fields: ['name', 'serial_number', 'station_truck', 'branch'],
  },
};

// ---------------------------------------------------------------------------
// Derived helpers
// ---------------------------------------------------------------------------

// The two middle fields of a label's text block — the ones between the name and
// the branch. Inventory reads "SKU-1 · ea"; a tool reads "SN-4471 · Truck 12".
//
// Session 76. Added because the Canon tile hardcoded `sku · uom` and the route
// `/inventory/scan/:id` for EVERY subject, which was invisible while the modal
// was wired to nothing. The moment FleetPage reaches it, a tool batch sent to
// Canon is reachable (tools offer 2x1, and 2x1 is a Canon format) and would
// have printed inventory URLs onto vinyl — #320's permanence argument again.
//
// Derived from SUBJECT_TYPES.fields rather than a second hand-written map, so
// the gate's change 2 keeps its promise: a new subject is one registry entry.
export function labelDetailFields(subjectId) {
  const subject = getSubject(subjectId);
  if (!subject) return [];
  return subject.fields.filter(f => f !== 'name' && f !== 'branch');
}

// The rendered detail line for a subject's record, em-dashed where empty so a
// missing serial reads as missing rather than collapsing the separator.
export function labelDetailText(subjectId, record) {
  const fields = labelDetailFields(subjectId);
  if (fields.length === 0) return '';
  return fields.map(f => record?.[f] || '—').join(' · ');
}

export function getFormat(formatId) {
  return LABEL_FORMATS[formatId] || null;
}

export function getSubject(subjectId) {
  return SUBJECT_TYPES[subjectId] || null;
}

// Formats offered for a subject, in ladder order, optionally filtered to those
// the mobile sheet shows.
export function formatsForSubject(subjectId, { mobile = false } = {}) {
  const subject = getSubject(subjectId);
  if (!subject) return [];
  return FORMAT_ORDER
    .filter(id => subject.formats.includes(id))
    .map(id => LABEL_FORMATS[id])
    .filter(f => (mobile ? f.onMobile : true));
}

// Why a destination is unavailable for a format, or null if it is available.
//
// Callout C: unavailable destinations are shown greyed WITH A REASON, never
// hidden. A hidden Zebra row teaches nobody that Zebras exist or why this
// branch has none. This returns the reason string the modal displays, so the
// explanation lives next to the rule that produced it.
export function destinationBlockedReason(formatId, destinationId, { zebraCount = 0 } = {}) {
  const format = getFormat(formatId);
  if (!format) return 'Unknown label size';

  if (!format.destinations.includes(destinationId)) {
    if (destinationId === 'local') {
      // Session 76. The reason a large format cannot go to a desk printer is
      // no longer about 24mm tape - the destination is any printer the OS can
      // see, and Stede does not know which one that is. Two different reasons
      // live here and they must not be blurred:
      //
      //   24 x 30 is PHYSICALLY impossible on office equipment. It is a
      //   two-and-a-half foot poster. That one is permanent.
      //
      //   3 x 5, 4 x 6 and 8.5 x 11 are physically fine on paper - 8.5 x 11 is
      //   letter - and are withheld only because the browser-print template is
      //   a landscape layout (QR left, text right) that has no portrait
      //   variant. That is the same two-layout problem drawLabelTile already
      //   solved for the Canon tile, and it is buildable work rather than a
      //   law of physics. Say so, so nobody reads it as impossible.
      return shortEdgeIn(formatId) > 11
        ? `${format.label} is too large for any desk printer`
        : `${format.label} has no paper layout yet — Canon only for now`;
    }
    if (destinationId === 'zebra') {
      return format.zebraFuture
        ? `${format.label} — no Zebra stock for this size yet`
        : `${format.label} is not a Zebra format`;
    }
    return `${format.label} is not a Canon format`;
  }

  // No availability gate on `local`, deliberately. Every machine running a
  // browser has a print dialog, so this destination is always reachable; what
  // it cannot do is promise which printer is on the other end. The old gate
  // read branches.has_brother_printer, which answered a different question.
  if (destinationId === 'zebra' && zebraCount < 1) return 'No printer at this branch';

  return null;
}

// ---------------------------------------------------------------------------
// #321 - QR pixel width derived from physical label size
// ---------------------------------------------------------------------------

// The QR's physical size on a label. On the small formats it is capped by the
// SHORT edge, not the long one - which is the gate's point that a 2x1 and a
// 3x1 carry the same ~0.85" code, so the longer label buys text room and no
// extra scanning range at all.
//
// 0.12 is the proportion of the short edge given up to padding on each side,
// matching the 0.06" pad the Canon tile has used since Session 47 on a 1" tile.
// Anchored on the gate's own two stated figures rather than a round proportion:
// the 1" formats carry a 0.85" code, and the 24" wall label carries roughly 22"
// once margins are taken. A constant proportion cannot hit both - a big label
// gives up proportionally less to its margin - so this is linear with an offset.
//   1"    -> 0.85   (gate section 3, tool label)
//   24"   -> 21.55  (gate: "a 24" label carries a 22" code once margins are taken")
export function shortEdgeIn(formatId) {
  const format = getFormat(formatId);
  if (!format) return 1;
  return Math.min(format.widthIn, format.heightIn);
}

export function qrPhysicalWidthIn(formatId) {
  const format = getFormat(formatId);
  if (!format) return 0.85;
  // The SHORT edge governs, and it is not always heightIn - the large formats
  // are portrait (3x5 is 3 wide by 5 tall), the small ones landscape.
  return Math.max(0.25, shortEdgeIn(formatId) * 0.9 - 0.05);
}

// Pixel width to ask QRCode.toDataURL for.
//
// #321: this was hardcoded at 256 in two files regardless of label size. On the
// 24x30 wall label that is roughly 10 dots per inch across a 22-inch code.
//
// The rule: ~150 px per printed inch, floored at 256 so nothing regresses below
// today's small-label output, and capped at 2048 so a wall label does not embed
// a 40-megabyte PNG in the PDF. At the cap a 24x30 carries ~97 px/in, against
// ~10 before.
//
// Deliberately NOT stating a target dpi as the pass condition. Per #321: the
// passing result is a large-format tile PROVEN TO SCAN off real vinyl, at the
// distance its chip claims. The numbers here are what make that test worth
// running, not a substitute for it.
export const QR_PX_PER_INCH = 150;
export const QR_PX_MIN = 256;
export const QR_PX_MAX = 2048;

export function qrPixelWidth(formatId) {
  const inches = qrPhysicalWidthIn(formatId);
  const raw = Math.round(inches * QR_PX_PER_INCH);
  return Math.min(QR_PX_MAX, Math.max(QR_PX_MIN, raw));
}
